<template>
  <div class="flex w-full min-h-screen flex-col">
    <LAdminHubBanner v-if="authStore.isAdminHubSession" />
    <main class="relative flex w-full flex-1 flex-col md:flex-row bg-grey-light">
      <LNav v-if="authStore.isAuthenticated" />
      <LLanguageSwitcher :expand-toggle="langExpandToggle" @toggle-expand="toggleLangExpand" />
      <RouterView />
      <Toast />
    </main>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue'
import { RouterView } from 'vue-router'
import LNav from '@/components/global/LNav.vue'
import LLanguageSwitcher from '@/components/global/LLanguageSwitcher.vue'
import LAdminHubBanner from '@/components/global/LAdminHubBanner.vue'
import { useAuthStore } from '@/stores/auth'
import AuthService from '@/services/authServices'
import { useToast } from 'primevue/usetoast'
import { usePrimeVue } from 'primevue/config'
import { useI18n } from 'vue-i18n'
import { toastBus } from '@/utils/toastBus'
import { takeSwitchedCustomer } from '@/utils/customerSwitch'
import { primeVueLocaleDe } from '@/utils/primevue-locale'

const authStore = useAuthStore()
const toast = useToast()
const { t, locale } = useI18n()
toastBus.on((msg) => toast.add(msg))

// PrimeVue's own texts (the password field's prompt) follow the app's language
const primevue = usePrimeVue()
const primeVueLocaleEn = { ...primevue.config.locale }
watch(
  locale,
  (lang) =>
    Object.assign(primevue.config.locale!, lang === 'de' ? primeVueLocaleDe : primeVueLocaleEn),
  { immediate: true },
)

onMounted(async () => {
  // A customer switch reloads the app, so its confirmation is delivered here
  // rather than by the switcher itself.
  const switchedTo = takeSwitchedCustomer()
  if (switchedTo) {
    toast.add({
      severity: 'success',
      summary: t('nav.customerSwitch.switched', { name: switchedTo }),
    })
  }

  // An admin-hub session already has its user + customer name from the login
  // exchange (see loginWithAdminHubToken). Skip the /auth/user refresh — a
  // customer may not be a valid "user" there, and a 401 would wipe the session.
  if (!authStore.isAuthenticated || authStore.isAdminHubSession) return

  try {
    const user = await AuthService.getLoginUser()
    authStore.user = user
    localStorage.setItem('authUser', JSON.stringify(user))
  } catch {
    // token invalid/expired — let the router guard handle redirect
  }
})

const langExpandToggle = ref(false)

const toggleLangExpand = (value: boolean) => {
  langExpandToggle.value = value
}
</script>
